import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

// Loads (or creates) the public.users profile row for a logged-in auth user.
// Works whether or not there is an `on auth.users` trigger.
const ensureProfile = async (authUser) => {
  if (!authUser) return null;
  const { data: existing, error } = await supabase
    .from("users")
    .select("*")
    .eq("id", authUser.id)
    .maybeSingle();

  if (existing) return existing;
  if (error && error.code !== "PGRST116") {
    // PGRST116 = no rows; other errors are real (likely RLS); just return minimal info
    return { id: authUser.id, email: authUser.email, role: "user" };
  }

  // Try to insert a row (requires INSERT policy for auth.uid() = id)
  const { data: inserted } = await supabase
    .from("users")
    .insert({ id: authUser.id, email: authUser.email, role: "user" })
    .select()
    .maybeSingle();
  return inserted || { id: authUser.id, email: authUser.email, role: "user" };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // public.users row (with role)
  const [authUser, setAuthUser] = useState(null); // raw supabase auth user
  const [loading, setLoading] = useState(true);

  const syncFromSession = useCallback(async (session) => {
    if (!session?.user) {
      setUser(null);
      setAuthUser(null);
      return;
    }
    setAuthUser(session.user);
    const profile = await ensureProfile(session.user);
    setUser(profile);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      await syncFromSession(session);
      setLoading(false);
    })();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (mounted) syncFromSession(session);
    });
    return () => { mounted = false; subscription?.unsubscribe(); };
  }, [syncFromSession]);

  const loginEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    const profile = await ensureProfile(data.user);
    setUser(profile);
    return profile;
  };

  const register = async (email, password, extra = {}) => {
    let signUpRes;
    try {
      signUpRes = await supabase.auth.signUp({
        email, password,
        options: { data: { ...extra } },
      });
    } catch (raw) {
      // supabase-js v2 sometimes throws on 4xx (rate-limit, body-stream bug, …)
      // BEFORE we ever see {data, error}. Translate to a friendly Error.
      const msg = raw?.message || "";
      const e = new Error("No se pudo crear la cuenta. Inténtalo de nuevo en unos minutos.");
      if (/body stream|rate.?limit|429/i.test(msg)) {
        e.message = "Demasiados intentos. Espera unos minutos antes de volver a probar.";
        e.code = "rate_limit";
      } else {
        e.code = raw?.code || raw?.name || "signup_failed";
      }
      throw e;
    }
    const { data, error } = signUpRes || {};
    if (error) throw error;
    // If sign-up returned a session, we're logged in
    if (data?.session && data?.user) {
      const profile = await ensureProfile(data.user);
      setUser(profile);
      return profile;
    }
    // Email-confirmation flow (or any other "no session" path): try to log
    // in immediately with the same creds. If that fails for ANY reason
    // (email not confirmed, rate-limit, supabase-js body-stream bug, …)
    // we surface it as `needsEmailConfirmation` so the UI can show a
    // friendly "check your email" instead of a cryptic toast.
    if (data?.user) {
      try {
        const { data: signIn, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (!signInError && signIn?.user) {
          const profile = await ensureProfile(signIn.user);
          setUser(profile);
          return profile;
        }
        if (signInError) throw signInError;
      } catch (signInErr) {
        const e = new Error(signInErr?.message || "Email confirmation required");
        e.code = signInErr?.code || signInErr?.name;
        e.needsEmailConfirmation = true;   // fail-safe: don't expose vendor internals
        throw e;
      }
    }
    return null;
  };

  const loginGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setAuthUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, authUser, loading, loginEmail, register, loginGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
